import type {Dispatch, ReactElement, SetStateAction} from 'react'
import React, {useState, useEffect} from 'react'
import type {Libraries} from '@react-google-maps/api'
import {useJsApiLoader} from '@react-google-maps/api'
import {SearchLocationInput} from '@/products/find-a-business/search-location-input'
import type {Marker} from '@/models/page-props'
import type {
	BusinessTypeObj,
	FilterState,
	LocationProps,
	LocationState,
	SearchLocationLatLongObj,
	TagsObj,
} from '@/utilities/types'
import {Filter} from './filter'
import {BusinessMap} from './business-map'

const libraries: Libraries = ['geometry', 'places']

interface BusinessMapWithSearchFilterAndInfoWindowProps {
	className?: string
	title?: string
	description?: string
	apiKey: string
	markers: Marker[] // You can replace 'object' with a more specific type if available
	businessTypes: BusinessTypeObj[]
	tags: TagsObj[]
	currentLocationText?: string
	placeholderText?: string
	label?: string
	countries?: string[]
	businessFilterTitle?: string
	tagsFilterTitle?: string
	filterButtonText?: string
	filterResetText?: string
	directionsText?: string
	defaultSouthBound?: number
	defaultWestBound?: number
	defaultNorthBound?: number
	defaultEastBound?: number
	locationState: LocationState
	setLocationState: Dispatch<SetStateAction<LocationState>>
	searchLocationLatLong: SearchLocationLatLongObj
	setSearchLocationLatLong: Dispatch<SetStateAction<SearchLocationLatLongObj>>
	enterLocationPrompt: string
	filterState: FilterState
	handleFilterUpdate: (newState: FilterState) => void
	enableSearch: boolean
	permission: 'prompt' | 'denied' | 'granted'
	radius: number
	searchAreaAccentColour: string
	searchAreaOpacity: number | null
}
export const BusinessMapWithSearchFilterAndInfoWindow = ({
	className = '',
	markers = [],
	title,
	description,
	defaultSouthBound,
	defaultWestBound,
	defaultNorthBound,
	defaultEastBound,
	apiKey,
	businessTypes = [],
	tags = [],
	currentLocationText = 'Search by current location',
	placeholderText = 'Enter your town or postcode',
	label = 'Your location',
	countries = [],
	businessFilterTitle,
	tagsFilterTitle,
	filterButtonText,
	filterResetText,
	directionsText,
	enterLocationPrompt,
	setLocationState,
	searchLocationLatLong,
	setSearchLocationLatLong,
	enableSearch,
	permission,
	radius,
	searchAreaAccentColour,
	searchAreaOpacity,
}: BusinessMapWithSearchFilterAndInfoWindowProps): ReactElement => {
	const [data, setData] = useState<Marker[]>(markers)
	const [filterData, setFilterData] = useState<{
		businessTypeIds: string[]
		tagIds: string[]
	}>({
		businessTypeIds: [],
		tagIds: [],
	})

	const [searchLocation, setSearchLocation] = useState<LocationProps>({
		searchName: '',
		lat: 0,
		lng: 0,
	})

	const showFilters = businessTypes.length > 1 || tags.length > 1
	const defaultBounds = {
		south: defaultSouthBound || 0,
		west: defaultWestBound || 0,
		north: defaultNorthBound || 0,
		east: defaultEastBound || 0,
	}
	const {isLoaded} = useJsApiLoader({
		googleMapsApiKey: apiKey,
		libraries,
	})

	useEffect(() => {
		const {businessTypeIds, tagIds} = filterData
		let filteredMarkers: Marker[] = []
		if (businessTypeIds.length === 0 && tagIds.length === 0) {
			filteredMarkers = markers
		} else if (businessTypeIds.length && tagIds.length) {
			filteredMarkers = markers.filter(
				(marker) =>
					businessTypeIds.includes(
						marker.accountClassification ?? ''
					) &&
					marker.tags?.length &&
					marker.tags.find((tag) => tagIds.includes(tag))
			)
		} else if (businessTypeIds.length === 0 && tagIds.length) {
			filteredMarkers = markers.filter(
				(marker) =>
					marker.tags?.length &&
					marker.tags.find((tag) => tagIds.includes(tag))
			)
		} else if (businessTypeIds.length && tagIds.length === 0) {
			filteredMarkers = markers.filter((marker) =>
				businessTypeIds.includes(marker.accountClassification ?? '')
			)
		}

		if (filteredMarkers.length === 0) {
			filteredMarkers = markers
		}

		setData(filteredMarkers)
	}, [filterData, markers])

	return (
		<div className={`${className} flex-col md:justify-center`}>
			<div className="flex flex-col justify-between space-y-4 p-4 md:flex-row md:items-end md:space-y-0 md:p-0">
				<SearchLocationInput
					className={className}
					countries={countries}
					currentLocationText={currentLocationText}
					enableSearch={enableSearch}
					isMapLoaded={isLoaded}
					label={label}
					permission={permission}
					placeholderText={placeholderText}
					searchLocationLatLong={searchLocationLatLong}
					setLocationState={setLocationState}
					setSearchLocation={setSearchLocation}
					setSearchLocationLatLong={setSearchLocationLatLong}
				/>
				{showFilters ? (
					<Filter
						businessFilterTitle={businessFilterTitle ?? ''}
						buttonText={filterButtonText ?? ''}
						filterOptions={{businessTypes, tags}}
						resetText={filterResetText ?? ''}
						setFilterData={setFilterData}
						tagsFilterTitle={tagsFilterTitle ?? ''}
					/>
				) : null}
			</div>
			<div className="relative">
				<BusinessMap
					defaultBounds={defaultBounds}
					description={description ?? ''}
					directionsText={directionsText ?? ''}
					enterLocationPrompt={
						!searchLocationLatLong.searchName?.length
							? enterLocationPrompt
							: ''
					}
					isLoaded={isLoaded}
					markers={data}
					radius={radius}
					searchAreaAccentColour={searchAreaAccentColour}
					searchAreaOpacity={searchAreaOpacity}
					searchLocation={searchLocation}
					title={title ?? ''}
				/>
			</div>
		</div>
	)
}
